/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WarehouseDetailPage.tsx
 *     Description     :   Detail view for a single warehouse with tabbed sections
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-02
 *     Constitution    :   v1.12.0
 *     Feature         :   warehouse-management
 * =========================================================================================
 *
 * Tabs:
 * - Overview: Basic warehouse information and status
 * - Configuration: Warehouse settings and metadata
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageData } from '../../../hooks/usePageData';
import {
  ArrowLeft,
  Package,
  CheckCircle2,
  AlertCircle,
  Database,
} from 'lucide-react';
import { WarehouseStorageList } from '../components/storage/WarehouseStorageList';
import { AddWarehouseStorageAccountForm } from '../components/storage/AddWarehouseStorageAccountForm';
import { WorkflowProgressBanner } from '../../workflows/components/WorkflowProgressBanner';
import { GuidedEmptyState } from '../../../components/EmptyState/GuidedEmptyState';
import { useEntityWorkflowStatus } from '../../workflows/hooks/useWorkflowStatus';
import {
  AppTooltip,
  Modal,
  Card,
  Alert,
  Badge,
  Button,
  Tabs,
  DataTable,
  Input,
  LoadingState,
  EmptyState,
  DetailLayout,
  RefreshButton,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { useWarehouseTables } from '../../../services/operations/warehousesService';
import type { WarehouseTable } from '../../../types/operations';
import { Search } from 'lucide-react';
import { PageHelpPanel } from '../../../components/ui/PageHelpPanel';
import { warehouseDetailHelp, warehouseFieldTooltips } from '../help/warehouseDetailHelp';
import { formatWarehouseSize, humanBytes } from '../utils/entitySize';
import { oneLakeItemUrl } from '../../../utils/oneLake';
import { retiredLabel } from '@/utils/retiredLabel';
import api from '../services/api';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';
import { ProtectionPanel } from '@/components/backup/ProtectionPanel';
import { AccessGate } from '../../../components/access/AccessGate';
import { useHasAccess } from '../../../hooks/useAccess';

// ============================================================================
// Types
// ============================================================================

interface Warehouse {
  id: string;
  fabric_id: string;
  workspace_id: string;
  workspace_name: string;
  display_name: string;
  description: string | null;
  warehouse_type: string | null;
  status: string;
  is_active: boolean;
  /** Spec 297 T032: the retirement stamp; the status chip says "retired on" from it. */
  deactivated_at?: string | null;
  storage_size_gb: number | null;
  /** Why the size could not be measured; null when it was measured. */
  size_measurement_error: string | null;
  /** Active tables in the warehouse's table inventory. */
  table_count: number;
  connection_string: string | null;
  collation_type: string | null;
  /** When Fabric created the warehouse; created_at is when this platform first recorded it. */
  created_date: string | null;
  capacity_id: string | null;
  capacity_name: string | null;
  capacity_type: number | null;
  created_at: string;
  updated_at: string;
  discovered_at: string | null;
  last_seen_at: string | null;
  /** Spec 286 T159 item 6: discovered_at and last_seen_at are written by no collector; this is. */
  last_changed_at: string | null;
  /** Spec 286 T159 item 27: when Fabric last updated the warehouse (Admin - Items - List Items lastUpdatedDate). */
  fabric_last_updated_at: string | null;
}

type TabId = 'overview' | 'schema' | 'storage' | 'config';

// Format date for display
function formatDate(date: string | null | undefined): string {
  if (!date) return 'Never';
  return formatEastern(date);
}

// The size as the warehouse list shows it; an unmeasured size carries its recorded reason, and the size the merge kept
// from an earlier collection as the last known one (Spec 286 T159 item 32).
function storageSizeText(w: Warehouse): string {
  const size = formatWarehouseSize(w.storage_size_gb, w.table_count, w.size_measurement_error);
  if (size.state !== 'not-measured') return size.text;
  const last = size.lastKnownGb != null ? `. Last known size: ${humanBytes(size.lastKnownGb * 1024 ** 3)}` : '';
  return `${size.text}: ${size.reason}${last}`;
}

// ============================================================================
// Component
// ============================================================================

export function WarehouseDetailPage({
  embeddedId,
  onClose,
}: {
  embeddedId?: string;
  onClose?: () => void;
} = {}): React.ReactElement {
  const params = useParams<{ id: string }>();
  const id = embeddedId ?? params.id;
  // When hosted in a modal (embeddedId/onClose) drop the page chrome + close via onClose.
  const embedded = onClose != null;
  const navigate = useNavigate();
  const handleBack = () => (onClose ? onClose() : navigate('/warehouses'));
  // Spec 285 (owner: gate ALL backup functionality): without the backup license the protection
  // panel, the backup Storage Accounts mapping tab and the backup-onboarding banner are hidden.
  const canSeeBackup = useHasAccess('backup');

  // State
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showAddStorage, setShowAddStorage] = useState(false);

  // Workflow status for progress banner
  // The warehouse workflow is BACKUP onboarding (… → assign storage account → …).
  const { data: workflowStatus, isLoading: workflowLoading } = useEntityWorkflowStatus(
    'warehouse',
    id ?? '',
    !!id && canSeeBackup
  );

  // Fetch warehouse. Spec 226: through usePageData, so a refresh keeps the warehouse on screen and a
  // failed one shows the Refresh badge; the error view below is for a warehouse that never loaded.
  const query = useQuery({
    queryKey: ['warehouse-detail', id],
    queryFn: async () => (await api.get<{ success: boolean; data: Warehouse }>(`/api/v1/warehouses/${id}`)).data,
    enabled: !!id,
  });
  const { data: warehouse, isLoading: loading, error: loadError, refresh, isRefreshing, lastFetchedAt } = usePageData(query);
  const error = warehouse ? null : loadError
    ? (loadError instanceof Error ? loadError.message : 'Failed to fetch warehouse')
    : null;

  // Loading state
  if (loading) {
    return (
      <div className="mx-auto mt-8 w-full max-w-screen-xl px-4">
        <div className="animate-pulse rounded bg-[var(--color-bg-subtle)]" style={{ width: 300, height: 40 }} />
        <div className="mt-4 animate-pulse rounded bg-[var(--color-bg-subtle)]" style={{ height: 200 }} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mx-auto mt-8 w-full max-w-screen-xl px-4">
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
        <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={handleBack}>
          Back to Warehouses
        </Button>
      </div>
    );
  }

  // Not found
  if (!warehouse) {
    return (
      <div className="mx-auto mt-8 w-full max-w-screen-xl px-4">
        <Alert variant="warning">Warehouse not found</Alert>
        <Button
          variant="outline"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={handleBack}
          className="mt-4"
        >
          Back to Warehouses
        </Button>
      </div>
    );
  }

  const allTabs = [
    { key: 'overview', label: 'Overview' },
    // Spec 216 T030: the schema browser lives here now (the old fleet-wide
    // Warehouse Schema screen redirects to the Warehouse List).
    { key: 'schema', label: 'Schema' },
    { key: 'storage', label: 'Storage Accounts', icon: <Database className="h-4 w-4" /> },
    { key: 'config', label: 'Configuration' },
  ];
  const tabs = canSeeBackup ? allTabs : allTabs.filter((t) => t.key !== 'storage');
  const effectiveTab: TabId = !canSeeBackup && activeTab === 'storage' ? 'overview' : activeTab;

  // Spec 297 T032: a retired warehouse says when (its stamp) or that it is (its flag); the lists no longer carry it.
  const retired = retiredLabel(warehouse);
  const body = (
    <>
      {/* Status Chips (T099) */}
      <div className="mb-6 flex gap-2">
        <AppTooltip title={warehouseFieldTooltips.status}>
          <Badge
            variant={retired ? 'default' : 'success'}
            leftIcon={
              retired ? (
                <AlertCircle className="h-3 w-3" />
              ) : (
                <CheckCircle2 className="h-3 w-3" />
              )
            }
          >
            {retired ?? 'Active'}
          </Badge>
        </AppTooltip>
        {/* Spec 286 T159 item 27: the type Fabric reported, never an invented "Standard" for a missing one. */}
        {warehouse.warehouse_type && (
          <AppTooltip title={warehouseFieldTooltips.warehouseType}>
            <Badge variant="secondary" isOutlined>
              {warehouse.warehouse_type}
            </Badge>
          </AppTooltip>
        )}
        {warehouse.capacity_name && (
          <AppTooltip title={warehouseFieldTooltips.capacityType}>
            <Badge variant="secondary" isOutlined>
              {`Capacity: ${warehouse.capacity_name}`}
            </Badge>
          </AppTooltip>
        )}
      </div>

      {/* Workflow Progress Banner (Feature 135) — backup onboarding, so backup-licensed only. */}
      {canSeeBackup && (
        <div className="mb-6">
          <WorkflowProgressBanner status={workflowStatus ?? null} isLoading={workflowLoading} />
        </div>
      )}

      {/* Tabs */}
      <Tabs
        tabs={tabs}
        value={effectiveTab}
        onChange={(key) => setActiveTab(key as TabId)}
        className="mb-6"
      />

      {/* Tab Content */}
      {effectiveTab === 'overview' && (
        <div className="space-y-6">
        {/* 🛑 Until now NO page anywhere showed a warehouse's backup state — this page fetched
            only the entity, not even its run history. A warehouse could be unprotected, never
            verified, or bound to no storage account and the UI had no way to say so. Full width
            and first, because "is this protected?" outranks the descriptive fields below it. */}
        <AccessGate area="backup">
          <ProtectionPanel entityType="warehouse" entityId={params.id ?? ''} />
        </AccessGate>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Basic Info */}
          <Card padding="lg">
            <h3 className="mb-1 text-lg font-semibold text-[var(--color-text-primary)]">
              Warehouse Information
            </h3>
            <hr className="mb-4 border-[var(--color-border)]" />
            <InfoRow label="Display Name" value={warehouse.display_name} />
            <InfoRow label="Fabric ID" value={warehouse.fabric_id} monospace />
            <InfoRow label="Warehouse ID" value={warehouse.id} monospace />
            <AppTooltip title={warehouseFieldTooltips.warehouseType}>
              <div>
                <InfoRow label="Type" value={warehouse.warehouse_type || '—'} />
              </div>
            </AppTooltip>
            <InfoRow
              label="Status"
              value={warehouse.status || (warehouse.is_active ? 'Active' : 'Inactive')}
            />
            <InfoRow label="Description" value={warehouse.description || '—'} />
            <InfoRow label="Storage Size" value={storageSizeText(warehouse)} />
            <InfoRow label="Collation" value={warehouse.collation_type || '—'} />
            <InfoRow label="Connection String" value={warehouse.connection_string || '—'} monospace />
            <InfoRow
              label="Created in Fabric"
              value={warehouse.created_date ? formatEastern(warehouse.created_date) : '—'}
            />
            <InfoRow
              label="OneLake path"
              value={oneLakeItemUrl(warehouse.workspace_id, warehouse.fabric_id) ?? '—'}
              monospace
            />
          </Card>

          {/* Workspace & Capacity */}
          <Card padding="lg">
            <h3 className="mb-1 text-lg font-semibold text-[var(--color-text-primary)]">
              Workspace & Capacity
            </h3>
            <hr className="mb-4 border-[var(--color-border)]" />
            <InfoRow label="Workspace" value={warehouse.workspace_name} />
            <InfoRow label="Workspace ID" value={warehouse.workspace_id} monospace />
            <InfoRow label="Capacity" value={warehouse.capacity_name || '—'} />
            {warehouse.capacity_id && (
              <InfoRow label="Capacity ID" value={warehouse.capacity_id} monospace />
            )}
            {warehouse.capacity_type !== null && warehouse.capacity_type !== undefined && (
              <InfoRow label="Capacity Type" value={String(warehouse.capacity_type)} />
            )}
          </Card>

          {/* Timestamps */}
          <Card padding="lg" className="md:col-span-2">
            <h3 className="mb-1 text-lg font-semibold text-[var(--color-text-primary)]">Timestamps</h3>
            <hr className="mb-4 border-[var(--color-border)]" />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
              <div>
                <p className="text-xs text-[var(--color-text-secondary)]">Created</p>
                <p className="text-sm text-[var(--color-text-primary)]">{formatDate(warehouse.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-[var(--color-text-secondary)]">Updated</p>
                <p className="text-sm text-[var(--color-text-primary)]">{formatDate(warehouse.updated_at)}</p>
              </div>
              <div>
                <AppTooltip title={warehouseFieldTooltips.lastChangedAt}>
                  <p className="text-xs text-[var(--color-text-secondary)]">Last changed</p>
                </AppTooltip>
                <p className="text-sm text-[var(--color-text-primary)]">{formatDate(warehouse.last_changed_at)}</p>
              </div>
              <div>
                <AppTooltip title={warehouseFieldTooltips.fabricLastUpdatedAt}>
                  <p className="text-xs text-[var(--color-text-secondary)]">Changed in Fabric</p>
                </AppTooltip>
                <p className="text-sm text-[var(--color-text-primary)]">
                  {warehouse.fabric_last_updated_at ? formatEastern(warehouse.fabric_last_updated_at) : 'Not collected'}
                </p>
              </div>
            </div>
          </Card>
        </div>
        </div>
      )}

      {effectiveTab === 'schema' && <WarehouseSchemaTab warehouseId={warehouse.id} />}

      {effectiveTab === 'storage' && (
        <Card padding="lg">
          <WarehouseStorageList
            warehouseId={warehouse.id}
            onAddNew={() => setShowAddStorage(true)}
            emptyState={
              <GuidedEmptyState
                icon="storage"
                title="No Storage Accounts Mapped"
                description="Map a storage account to this warehouse to enable backup operations and data management."
                actionLabel="Add Storage Account"
                workflowStep="Step 3 of 4: Assign Storage Account"
              />
            }
          />
        </Card>
      )}

      {effectiveTab === 'config' && (
        <Card padding="lg">
          <h3 className="mb-1 text-lg font-semibold text-[var(--color-text-primary)]">Configuration</h3>
          <hr className="mb-4 border-[var(--color-border)]" />
          <Alert variant="info">
            Warehouse configuration management will be available in a future update. Currently,
            warehouses are automatically discovered and tracked via the collection job.
          </Alert>
        </Card>
      )}

      {/* Add Storage Account Modal — canonical kit Modal (Spec 197 design blueprint):
          transparent-but-blocking backdrop, framed, movable, no outside-close. */}
      {warehouse && (
        <Modal
          isOpen={showAddStorage}
          onClose={() => setShowAddStorage(false)}
          title="Add Storage Account"
          size="lg"
        >
          <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
            Select a storage account for backup operations on{' '}
            <span className="font-medium text-[var(--color-text-primary)]">{warehouse.display_name}</span>
          </p>
          <AddWarehouseStorageAccountForm
            warehouseId={warehouse.id}
            onSuccess={() => setShowAddStorage(false)}
            onCancel={() => setShowAddStorage(false)}
          />
        </Modal>
      )}
    </>
  );

  // In a list page's modal the page has its own h1, so the name is an h2 here.
  if (embedded) {
    return (
      <div className="mx-auto w-full max-w-screen-xl px-4 py-2">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold text-[var(--color-text-primary)]">{warehouse.display_name}</h2>
            {warehouse.description && (
              <p className="text-sm text-[var(--color-text-secondary)]">{warehouse.description}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastFetchedAt={lastFetchedAt} error={loadError} />
            <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={handleBack}>
              Back
            </Button>
          </div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <DetailLayout
      title={warehouse.display_name}
      subtitle={warehouse.description ?? undefined}
      icon={<Package className="h-6 w-6" />}
      backTo="/warehouses"
      backLabel="Back to Warehouses"
      actions={<PageHelpPanel content={warehouseDetailHelp} />}
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={loadError}
    >
      {body}
    </DetailLayout>
  );
}

// ============================================================================
// Info Row Component
// ============================================================================

interface InfoRowProps {
  label: string;
  value: string;
  monospace?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, monospace }) => (
  <div className="mb-3">
    <p className="text-xs text-[var(--color-text-secondary)]">{label}</p>
    <p
      className={
        monospace
          ? 'break-all font-mono text-[0.8rem] text-[var(--color-text-primary)]'
          : 'text-sm text-[var(--color-text-primary)]'
      }
    >
      {value}
    </p>
  </div>
);

// ============================================================================
// Schema tab (Spec 216 T030) — this warehouse's collected table catalog,
// reusing the /api/operations/warehouses/* service the old fleet-wide
// Warehouse Schema screen used.
// ============================================================================

const WarehouseSchemaTab: React.FC<{ warehouseId: string }> = ({ warehouseId }) => {
  const [search, setSearch] = useState('');
  const tables = useWarehouseTables({ warehouseId, search: search || undefined, pageSize: 200 });
  const rows = tables.data?.data ?? [];

  const formatStorage = (kb: number): string => {
    if (kb >= 1_048_576) return `${(kb / 1_048_576).toFixed(1)} GB`;
    if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
    return `${kb} KB`;
  };

  const columns: DataTableColumn<WarehouseTable>[] = [
    { key: 'table_name', header: 'Table', width: '38%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'schema_name', header: 'Schema', width: '16%' },
    {
      key: 'row_count',
      header: 'Rows',
      width: '16%',
      align: 'right',
      render: (t) => Number(t.row_count ?? 0).toLocaleString(),
    },
    {
      key: 'storage_kb',
      header: 'Storage',
      width: '14%',
      align: 'right',
      render: (t) => formatStorage(t.storage_kb ?? 0),
    },
    {
      key: 'last_updated',
      header: 'Last Updated',
      width: '16%',
      cellClassName: 'text-[var(--color-text-secondary)]',
      render: (t) => (t.last_updated ? formatEasternDate(t.last_updated) : '—'),
    },
  ];

  return (
    <Card padding="lg">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">Schema</h3>
        <div className="w-full sm:w-64">
          <Input
            isFullWidth
            placeholder="Search tables…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>
      {tables.isLoading ? (
        <LoadingState rows={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No table catalog collected"
          message="The schema scan collects warehouse tables each daily run."
        />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(t) => `${t.schema_name}.${t.table_name}`} pageSize={15} />
      )}
    </Card>
  );
};

export default WarehouseDetailPage;
